import { memo, ReactElement, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useExternalFilterBridge } from '../../hooks/useExternalFilterBridge';
import { FormToDataGridConnectorProps } from '../../types/FormToDataGridConnectorProps';
export type ExternalFiltersPortalProps = {
  externalFilterId: string;
  filterComponent: (props: FormToDataGridConnectorProps) => ReactElement;
};

/**
 * Renders a `filterComponent` into an external DOM element via React portal.
 *
 * Delegates store↔filter bridging to `useExternalFilterBridge`.
 * The portal mechanism uses `useLayoutEffect` + state for DOM element lookup
 * to avoid reads during render (React Compiler safe).
 *
 * ## Performance
 *
 * - `memo()` prevents re-renders from unrelated store changes propagating through EagGridTable.
 * - Store bridging is handled by `useExternalFilterBridge` (memoized values + stable onChange).
 * - DOM element lookup runs once per `externalFilterId` change.
 */
export const ExternalFiltersPortal = memo((props: ExternalFiltersPortalProps) => {
  const {
    values,
    onChange
  } = useExternalFilterBridge();
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);
  useLayoutEffect(() => {
    setPortalTarget(document.getElementById(props.externalFilterId)); // eslint-disable-line react-hooks/set-state-in-effect -- intentional: DOM element for portal target is not available during render; useLayoutEffect is the correct mechanism to read the DOM after mount
  }, [props.externalFilterId]);
  const FilterComponent = props.filterComponent;
  if (!portalTarget) {
    return null;
  }
  return createPortal(<FilterComponent values={values} onChange={onChange} />, portalTarget);
});